import React, { useState, useMemo, useEffect } from "react";
import { Card, Row, Col, Typography, Avatar, Space, Divider, Empty } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faDesktop } from "@fortawesome/free-solid-svg-icons";
import { SelectInput } from "../../../blueplanitUtils/InputField";
import { useQuery } from "@apollo/client";
import { GET_DEVICES, GET_LATEST_USER_SYNC_TIME, GET_LATEST_DEVICE_SYNC_TIME } from "../../../Queries/Activity";
import dayjs from "dayjs";
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { setLoader } from "../../../Slices/loaderSlice";
 
const { Text, Title } = Typography;
 
const EmployeeActivity = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
 
    const employees = useSelector((state) => state.employee.AllEmployees)
    const allEmployees = employees.filter(emp => emp.employeestatus === "Active");
    const { data, loading: devicesLoading, error: devicesError } = useQuery(GET_DEVICES, {
        fetchPolicy: "network-only",
    });
 
    useEffect(() => {
        dispatch(setLoader(devicesLoading));
    }, [devicesLoading]);
 
 
    // Department filter
    const allDepartments = useMemo(() => {
        if (!allEmployees) return [];
        return [...new Set(allEmployees.map((emp) => emp.department).filter(Boolean))];
    }, [allEmployees]);
 
    const [selectedDepartment, setSelectedDepartment] = useState([]);
 
    // Filter employees by department
    const filteredEmployees = useMemo(() => {
        if (!allEmployees) return [];
        if (!selectedDepartment || selectedDepartment.length === 0) return allEmployees;
        return allEmployees.filter((emp) => selectedDepartment.includes(emp.department));
    }, [allEmployees, selectedDepartment]);
 
    const groupedEmployees = useMemo(() => {
        if (!filteredEmployees || filteredEmployees.length === 0) return {};
 
        const groups = {};
 
        filteredEmployees.forEach((emp) => {
            let department = emp.department?.trim();
 
            if (!department) {
                department = "Other";
            } else if (department.toLowerCase().includes("both")) {
                department = "Both";
            }
 
            if (!groups[department]) {
                groups[department] = [];
            }
 
            groups[department].push(emp);
        });
 
        return groups;
    }, [filteredEmployees]);

    const { data: userSyncData, loading: userSyncLoading } = useQuery(GET_LATEST_USER_SYNC_TIME, {
        fetchPolicy: "network-only",
    });

    const { data: deviceSyncData, loading: deviceSyncLoading } = useQuery(GET_LATEST_DEVICE_SYNC_TIME, {
        fetchPolicy: "network-only",
    });

   
    // Get the latest sync timestamp for the current user or device
    // Prepare device -> users mapping
    const devicesArray = useMemo(() => {
        if (!data?.getDeviceData?.response) return [];
 
        // ✅ filter directly for records with userId === null
        return data.getDeviceData.response.filter(
            ({ userId }) => userId === null
        );
    }, [data]);
 
    return (
        <div style={{ padding: 16 }}>
            <Title level={3}>All Employees</Title>
 
            {/* Department Filter */}
            <div style={{ marginBottom: 16 }}>
                <SelectInput
                    style={{ width: 200 }}
                    placeholder="Select Department(s)"
                    mode="multiple"
                    value={selectedDepartment}
                    onChange={setSelectedDepartment}
                    allowClear
                    options={allDepartments.map((dep) => ({ label: dep, value: dep }))}
                />
            </div>
            {filteredEmployees.length === 0 ? (
                <Empty description="No employees found" />
            ) : (
                <>
                    {Object.keys(groupedEmployees).map((department) => (
                        <div key={department} style={{ marginBottom: 32 }}>
 
                            {/* Department Heading */}
                            <Title level={4} style={{ marginBottom: 16 }}>
                                {department}
                            </Title>
 
                            <Row gutter={[16, 16]}>
                                {groupedEmployees[department].map((emp) => {
                                    const empId = emp._id || emp.id; // fallback if _id is missing
                                    const empLatestSync = userSyncData?.getLatestUserSyncTime?.find(
                                        u => u.userId === empId
                                    )?.timeStamp;
                                    const syncText = empLatestSync
                                        ? dayjs(Number(empLatestSync)).format("DD MMM YYYY, HH:mm:ss")
                                        : "N/A";

                                    const syncColor = empLatestSync ? "green" : "black";

                                    return (
                                        <Col key={emp.id} xs={24} sm={12} md={8} lg={6}>
                                            <Card
                                                hoverable
                                                onClick={() => navigate(`/activity/user/${emp.id}`)}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <Space align="center">
                                                    {emp.avatar ? (
                                                        <Avatar src={emp.avatar} size={48} />
                                                    ) : (
                                                        <Avatar icon={<FontAwesomeIcon icon={faUser} />} size={48} />
                                                    )}
                                                    <div
                                                        style={{
                                                            maxWidth: 120,
                                                            // overflow: "hidden",
                                                            // textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        <Text strong>
                                                            {`${emp.firstname} ${emp.lastname}`}
                                                        </Text>
                                                        <br />
                                                        <Text type="secondary" ellipsis>
                                                            {emp.department || "Other"}
                                                        </Text>
                                                        <br />
                                                        <Text style={{ color: syncColor, fontSize: 12 }}>
                                                            <FontAwesomeIcon icon={faSync} style={{ marginRight: 5, fontSize: 10 }} />
                                                            {syncText}
                                                        </Text>
                                                    </div>
                                                </Space>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </div>
                    ))}
                </>
            )}
 
            {/* Devices with no logged-in users */}
            <Divider />
            <Title level={4}>Devices with No Logged-in Users</Title>
            {devicesError && (
                <Text type="danger">
                    Failed to load device information.
                </Text>
            )}
 
            {!devicesError &&
                (devicesArray.length === 0 ? (
                    <Empty description="No devices found" />
                ) : (
                    <Row gutter={[16, 16]}>
                        {devicesArray.map((device) => {
                            const deviceLatestSync = deviceSyncData?.getLatestDeviceSyncTime?.find(
                                d => String(d.deviceId) === String(device.deviceId)
                            )?.timeStamp;
                            const syncText = deviceLatestSync
                                        ? dayjs(Number(deviceLatestSync)).format("DD MMM YYYY, HH:mm:ss")
                                        : "N/A";
                                   const syncColor = deviceLatestSync ? "green" : "black";
                            return (
                                <Col key={device.deviceId} xs={24} sm={12} md={8} lg={6}>
                                    <Card
                                        hoverable
                                        onClick={() => navigate(`/activity/device/${device.deviceId}`)}
                                        type="inner"
                                        style={{ cursor: "default" }}
                                        title={
                                            <div
                                                style={{
                                                    maxWidth: 120,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faDesktop} /> {device.deviceId}
                                            </div>
                                        }
                                    >
                                        <Text type="secondary"  style={{ color: syncColor }}>
                                            <FontAwesomeIcon icon={faSync} style={{  color: syncColor, marginRight: 5 }} />
                                            {syncText}  
                                        </Text>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                ))}
 
        </div>
    );
};
 
export default EmployeeActivity;